"use client";

import {
  DEFAULT_MODAL_FILTERS,
} from "libs/state/src/lib/stores/tanstack/filters/filters.config";
import {
  useFiltersStore,
} from "libs/state/src/lib/stores/tanstack/filters/useFiltersStore";

import type {
  TModalDataFor,
  TModalDataMap,
  TModalOpenArgs,
  TUseModals,
} from "@/ui/lib/hooks/Modals/useModals.config";

export const useModals = <
  TModal extends string,
  TDataMap extends TModalDataMap<TModal> = Record<never, never>,
>(): TUseModals<TModal, TDataMap> => {
  const { filters, replaceFilterGroup } = useFiltersStore();
  const [ activeModal ] = Object.entries(filters.modals);
  const data = activeModal?.[1].data ?? null;

  const isOpen = (key: TModal) => activeModal?.[0] === key;

  const getData = <TKey extends TModal>(
    key: TKey,
  ): TModalDataFor<TModal, TDataMap, TKey> | null => isOpen(key)
    ? data as TModalDataFor<TModal, TDataMap, TKey>
    : null;

  const onOpen = <TKey extends TModal>(
    key: TKey,
    ...[ modalData ]: TModalOpenArgs<TModal, TDataMap, TKey>
  ) => {
    const serializedData = Object.fromEntries(
      Object.entries(modalData ?? {}).filter(
        (entry): entry is [ string, string ] => (
          typeof entry[1] === "string" && entry[1].length > 0
        ),
      ),
    );

    replaceFilterGroup("modals", {
      [key]: {
        data: serializedData,
      },
    });
  };

  const onClose = () => replaceFilterGroup("modals", DEFAULT_MODAL_FILTERS);

  return {
    data,
    getData,
    isOpen,
    onOpen,
    onClose,
  };
};
